import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import PageLayout from '../../common/PageLayout';
import apiClient from '../../../utils/apiClient';
import LoginStep from './LoginStep';
import EmailStep from './EmailStep';
import VerificationStep from './VerificationStep';
import BirthDateStep from './BirthDateStep';
import ProfileSetupStep from './ProfileSetupStep';
import ResetPasswordStep from './ResetPasswordStep';
import NewPasswordStep from './NewPasswordStep';
const SignUpContainer = ({ initialStep }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [direction, setDirection] = useState(0);
    const [step, setStep] = useState(
        location.state?.step || initialStep || 'login'
    );
    const [formData, setFormData] = useState({
        username: '',
        email: location.state?.email || '',
        password: '',
    });
    const [error, setError] = useState('');

    const getPostAuthRedirect = () => {
        const directRedirect = searchParams.get('redirect');
        if (directRedirect) {
            return directRedirect;
        }

        let authRedirect = sessionStorage.getItem('auth_redirect');

        // Fallback: check Google OAuth state
        try {
            const stateParam = searchParams.get('state');
            if (stateParam) {
                const parsedState = JSON.parse(atob(stateParam));
                if (parsedState.redirect) {
                    authRedirect = parsedState.redirect;
                }
            }
        } catch (error) {
            console.warn('Could not parse Google OAuth state:', error);
        }

        if (authRedirect) {
            const url = new URL(authRedirect, window.location.origin);
            const nestedRedirect = url.searchParams.get('redirect');

            if (nestedRedirect) {
                return nestedRedirect;
            }

            // If auth started from plain /login or /signup, default to profile
            if (url.pathname === '/login' || url.pathname === '/signup') {
                return '/profile';
            }

            return `${url.pathname}${url.search}`;
        }

        return '/profile';
    };

    // Handle Google OAuth callback
    useEffect(() => {
        const handleGoogleCallback = async () => {
            const code = searchParams.get('code');
            if (!code) return;

            try {
                const response = await apiClient.post('/api/users/auth/google/callback/', { code });
                
                // Always store tokens and user data if provided
                if (response.data.tokens) {
                    localStorage.setItem('access_token', response.data.tokens.access);
                    localStorage.setItem('refresh_token', response.data.tokens.refresh);
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                }

                // Check profile setup status
                if (response.data.user && !response.data.user.profile_setup_complete) {
                    setFormData(prev => ({
                        ...prev,
                        email: response.data.user.email
                    }));
                    setDirection(1);
                    setStep('birthdate');
                } else {
                    const destination = getPostAuthRedirect();
                    sessionStorage.removeItem('auth_redirect');
                    navigate(destination, { replace: true });
                }
            } catch (error) {
                console.error('Google callback error:', error);
                const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Failed to complete Google sign in';
                setError(errorMessage);
                // Don't navigate away on error - let user see the error message
                setStep('login');
            }
        };

        if (location.pathname === '/auth/google/callback' && searchParams.get('code')) {
            handleGoogleCallback();
        }
    }, [location.pathname, searchParams, navigate, setDirection, setFormData]);

    const handleNavigateToLogin = () => {
        setDirection(-1);
        setStep('login');
    };

    const handleNavigateToSignup = (email, isVerification = false, isGoogleAuth = false, isPasswordReset = false, isPostLogin = false) => {
        setDirection(1);
        if (isVerification) {
            setFormData(prev => ({
                ...prev,
                email: email
            }));
            setStep('verification');
        } else if (isGoogleAuth || isPostLogin) {
            setFormData(prev => ({
                ...prev,
                email: email
            }));
            setStep('birthdate');  // Go to birth date step for both Google auth and post-login
        } else if (isPasswordReset) {
            setFormData(prev => ({
                ...prev,
                email: email
            }));
            setStep('reset-password');
        } else if (email) {
            setFormData(prev => ({
                ...prev,
                email: email
            }));
            setStep('email');
        } else {
            setStep('email');
        }
    };

    const handleLoginSuccess = () => {
        const destination = getPostAuthRedirect();
        sessionStorage.removeItem('auth_redirect');
        navigate(destination, { replace: true });
    };

    const handleEmailSubmit = async (emailData, setError) => {
        try {
            try {
                // Check if email exists
                const response = await apiClient.post('/api/users/auth/check-email/', {
                    email: emailData.email.toLowerCase()
                });

                if (response.data.exists) {
                    if (!response.data.verified) {
                        // Email exists but not verified - resend verification
                        await apiClient.post('/api/users/resend-verification/', {
                            email: emailData.email.toLowerCase()
                        });
                        setFormData(prev => ({
                            ...prev,
                            email: emailData.email
                        }));
                        setDirection(1);
                        setStep('verification');
                    } else {
                        // Email exists and is verified - show error
                        setError('An account with this email already exists. Click "Sign in" above to sign in.');
                    }
                } else {
                    // New email - proceed with registration
                    const registerResponse = await apiClient.post('/api/users/auth/register/', {
                        email: emailData.email.toLowerCase(),
                        profile_setup_complete: false,
                        username: 'temp_' + Math.random().toString(36).substr(2, 9) // Temporary username
                    });
                    
                    if (registerResponse.data) {
                        setFormData(prev => ({
                            ...prev,
                            email: emailData.email
                        }));
                        setDirection(1);
                        setStep('verification');
                    } else {
                        throw new Error('Registration failed. Please try again.');
                    }
                }
            } catch (error) {
                console.error('Email handling error:', error);
                throw error;
            }
        } catch (error) {
            console.error('Email submission error:', error);
            const errorData = error.response?.data;
            if (errorData) {
                if (errorData.email) {
                    setError(errorData.email[0]);
                } else if (errorData.detail) {
                    setError(errorData.detail);
                } else {
                    setError('Failed to process email. Please try again.');
                }
            } else {
                setError(error.message || 'An error occurred. Please try again.');
            }
        }
    };

    const handleBack = () => {
        setDirection(-1);  // Moving backward
        setStep('email');
    };

    const handleBackToBirthDate = () => {
        setDirection(-1);  // Moving backward
        setStep('birthdate');
    };

    const slideVariants = {
        enter: (direction) => ({
            x: direction > 0 ? 300 : -300,
            opacity: 0
        }),
        center: {
            x: 0,
            opacity: 1,
            position: 'relative',
            width: '100%'
        },
        exit: (direction) => ({
            x: direction < 0 ? 300 : -300,
            opacity: 0
        })
    };

    const handleBirthDateSuccess = () => {
        setDirection(1);
        setStep('profile');
    };

    const handleProfileSetupSubmit = async (profileData) => {
        try {
            // Update the existing user's profile
            const updateData = {
                username: profileData.username,
                password: profileData.password,
                avatar_color: profileData.avatar_color,
                jersey_number: profileData.jersey_number,
                profile_setup_complete: true,
                has_set_password: true
            };

            await apiClient.patch('/api/users/profile/', updateData);
            
            const destination = getPostAuthRedirect();
            sessionStorage.removeItem('auth_redirect');
            navigate(destination, { replace: true });
        } catch (error) {
            profileData.onError?.(error.response?.data || error.message);
        }
    };

    return (
        <PageLayout>
                <div className="relative bg-transparent px-3" style={{ minHeight: '400px' }}>
                    <AnimatePresence
                        initial={false}
                        mode="popLayout"
                        custom={direction}
                    >
                        <motion.div
                            key={step}
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{
                                x: { type: "spring", stiffness: 300, damping: 30 },
                                opacity: { duration: 0.2 }
                            }}
                            style={{
                                position: 'absolute',
                                width: '100%',
                                left: 0,
                                right: 0
                            }}
                        >
                            {step === 'login' && (
                                <LoginStep
                                    onNavigateToSignup={handleNavigateToSignup}
                                    onLoginSuccess={handleLoginSuccess}
                                />
                            )}
                            {step === 'email' && (
                                <EmailStep
                                    onSubmit={handleEmailSubmit}
                                    initialData={formData}
                                    onNavigateToLogin={handleNavigateToLogin}
                                    onNavigateToSignup={handleNavigateToSignup}
                                    handleLoginSuccess={handleLoginSuccess}
                                />
                            )}
                            {step === 'verification' && (
                                <VerificationStep
                                    email={formData.email}
                                    handleLoginSuccess={handleLoginSuccess}
                                    onVerificationSuccess={(data) => {
                                        // Store tokens if provided
                                        if (data.tokens) {
                                            localStorage.setItem('access_token', data.tokens.access);
                                            localStorage.setItem('refresh_token', data.tokens.refresh);
                                            localStorage.setItem('user', JSON.stringify(data.user));
                                        }
                                        // Move to birth date step
                                        setDirection(1);
                                        setStep('birthdate');
                                    }}
                                />
                            )}
                            {step === 'birthdate' && (
                                <BirthDateStep
                                    email={formData.email}
                                    onSuccess={handleBirthDateSuccess}
                                />
                            )}
                            {step === 'profile' && (
                                <ProfileSetupStep
                                    onSubmit={handleProfileSetupSubmit}
                                    initialData={{
                                        ...formData,
                                        has_set_password: JSON.parse(localStorage.getItem('user'))?.has_set_password || false
                                    }}
                                    onBack={handleBackToBirthDate}
                                    email={formData.email}
                                />
                            )}
                            {step === 'reset-password' && (
                                <ResetPasswordStep
                                    email={formData.email}
                                    onBack={handleNavigateToLogin}
                                />
                            )}
                            {step === 'new-password' && (
                                <NewPasswordStep />
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
        </PageLayout>
    );
};

export default SignUpContainer;
